/**
 * `shell/DemoExitButton.tsx` — the way out of a `/demo` session (#2779).
 *
 * A demo visitor has no account to sign out of: what they want is the
 * website again. The button calls `leaveDemo()`, which clears the stored
 * demo token (the server sweeps the team at its 2 h TTL) and hard-loads
 * `/`, where the signed-out gate paints the landing page.
 * Renders nothing outside a demo session.
 *
 * "Try another team" ends the demo the same way but opens the picker at
 * `/demo`, so the visitor can pick another industry or team (#2824).
 *
 * Two placements: `<DemoExitButton/>` sits in the top bar from `xl` up.
 * Below `xl` the bar has no room (it covered the logo at 390 px and
 * squeezed the team switcher to one letter at 900 px), so `<DemoExitRow/>`
 * gives the same button its own row under the bar.
 */
import { ArrowLeft, Shuffle } from "lucide-react";

import { useAuth } from "../../lib/AuthContext";
import { cn } from "../../lib/cn";
import { leaveDemo } from "../../lib/demoSession";
import { Button } from "../ui";

function ExitButton({ className, testid }: { className?: string; testid: string }) {
  return (
    <Button
      size="sm"
      className={cn("gap-1.5", className)}
      data-testid={testid}
      onClick={() => leaveDemo()}
    >
      <ArrowLeft className="h-4 w-4" aria-hidden="true" />
      Back to the website
    </Button>
  );
}

/** Ends this demo and opens the picker, to try another industry or team (#2824). */
function SwitchButton({ className, testid }: { className?: string; testid: string }) {
  return (
    <Button
      size="sm"
      variant="secondary"
      className={cn("gap-1.5", className)}
      data-testid={testid}
      onClick={() => leaveDemo("/demo")}
    >
      <Shuffle className="h-4 w-4" aria-hidden="true" />
      Try another team
    </Button>
  );
}

/** The top-bar placement, `xl` and up. */
export function DemoExitButton() {
  const { mode } = useAuth();
  if (mode !== "demo") return null;
  return (
    <div className="hidden items-center gap-2 xl:flex">
      <SwitchButton testid="demo-switch-button" />
      <ExitButton testid="demo-exit-button" />
    </div>
  );
}

/** Below `xl`: its own row under the top bar. */
export function DemoExitRow() {
  const { mode } = useAuth();
  if (mode !== "demo") return null;
  return (
    <div className="flex items-center justify-between gap-3 px-4 pb-3 sm:px-6 lg:px-8 xl:hidden">
      <span className="text-xs text-fg-muted">You are in the demo.</span>
      <div className="flex flex-wrap items-center justify-end gap-2">
        <SwitchButton className="h-auto min-h-tap" testid="demo-switch-row-button" />
        <ExitButton className="h-auto min-h-tap" testid="demo-exit-row-button" />
      </div>
    </div>
  );
}
